import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Upload, FileText, Image as ImageIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent } from '../ui/card';
import FileUpload from './FileUpload';

const ProductFormDialog = ({ open, onOpenChange, product, onSave }) => {
  const [formData, setFormData] = useState({
    category: 'bfsi',
    name_id: '',
    name_en: '',
    description_id: '',
    description_en: '',
    icon: 'box',
    image_url: '',
    brochure_url: '',
    brochure_name: '',
    features: [],
    benefits: [],
    technical_specs: []
  });

  const [newFeature, setNewFeature] = useState({ text_id: '', text_en: '' });
  const [newBenefit, setNewBenefit] = useState({ text_id: '', text_en: '' });

  useEffect(() => {
    if (product) {
      setFormData({
        category: product.id <= '8' ? 'bfsi' : 'industry',
        name_id: product.name_id || '',
        name_en: product.name_en || '',
        description_id: product.description_id || '',
        description_en: product.description_en || '',
        icon: product.icon || 'box',
        image_url: product.image_url || '',
        brochure_url: product.brochure_url || '',
        brochure_name: product.brochure_name || '',
        features: product.features || [],
        benefits: product.benefits || [],
        technical_specs: product.technical_specs || []
      });
    } else {
      // Reset form for new product
      setFormData({
        category: 'bfsi',
        name_id: '',
        name_en: '',
        description_id: '',
        description_en: '',
        icon: 'box',
        image_url: '',
        brochure_url: '',
        brochure_name: '',
        features: [],
        benefits: [],
        technical_specs: []
      });
    }
  }, [product, open]);

  const handleAddFeature = () => {
    if (newFeature.text_id && newFeature.text_en) {
      setFormData({
        ...formData,
        features: [...formData.features, { id: Date.now(), ...newFeature }]
      });
      setNewFeature({ text_id: '', text_en: '' });
    }
  };

  const handleRemoveFeature = (id) => {
    setFormData({
      ...formData,
      features: formData.features.filter(f => f.id !== id)
    });
  };

  const handleAddBenefit = () => {
    if (newBenefit.text_id && newBenefit.text_en) {
      setFormData({
        ...formData,
        benefits: [...formData.benefits, { id: Date.now(), ...newBenefit }]
      });
      setNewBenefit({ text_id: '', text_en: '' });
    }
  };

  const handleRemoveBenefit = (id) => {
    setFormData({
      ...formData,
      benefits: formData.benefits.filter(b => b.id !== id)
    });
  };

  const handleSubmit = () => {
    onSave(formData);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{product ? 'Edit Product' : 'Add New Product'}</DialogTitle>
          <DialogDescription>
            {product ? 'Update product information' : 'Add a new product to your portfolio'}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="media">Media</TabsTrigger>
            <TabsTrigger value="features">Features</TabsTrigger>
            <TabsTrigger value="benefits">Benefits</TabsTrigger>
            <TabsTrigger value="specs">Tech Specs</TabsTrigger>
          </TabsList>

          {/* Basic Info Tab */}
          <TabsContent value="basic" className="space-y-4 mt-4">
            <div>
              <Label>Category</Label>
              <Select 
                value={formData.category} 
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bfsi">BFSI</SelectItem>
                  <SelectItem value="industry">Various Industry</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name (Indonesian)</Label>
                <Input 
                  placeholder="Nama Produk" 
                  value={formData.name_id}
                  onChange={(e) => setFormData({ ...formData, name_id: e.target.value })}
                />
              </div>
              <div>
                <Label>Name (English)</Label>
                <Input 
                  placeholder="Product Name" 
                  value={formData.name_en}
                  onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Description (Indonesian)</Label>
              <Textarea 
                placeholder="Deskripsi produk..." 
                rows={3} 
                value={formData.description_id}
                onChange={(e) => setFormData({ ...formData, description_id: e.target.value })}
              />
            </div>

            <div>
              <Label>Description (English)</Label>
              <Textarea 
                placeholder="Product description..." 
                rows={3} 
                value={formData.description_en}
                onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
              />
            </div>

            <div>
              <Label>Icon Name (Lucide React)</Label>
              <Input 
                placeholder="e.g., smartphone, credit-card, users" 
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1">Visit lucide.dev for icon names</p>
            </div>
          </TabsContent>

          {/* Media Tab */}
          <TabsContent value="media" className="space-y-6 mt-4">
            {/* Product Image Section */}
            <Card>
              <CardContent className="pt-6">
                <FileUpload
                  type="image"
                  currentFile={formData.image_url}
                  onUploadSuccess={(url) => setFormData({ ...formData, image_url: url })}
                  accept="image/*"
                  label="Product Image"
                  maxSize={5}
                />
                <div className="mt-4">
                  <Label>Or enter image URL</Label>
                  <Input 
                    placeholder="https://example.com/image.jpg" 
                    value={formData.image_url || ''}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Product Brochure Section */}
            <Card>
              <CardContent className="pt-6">
                <FileUpload
                  type="brochure"
                  currentFile={formData.brochure_url}
                  onUploadSuccess={(url, filename) => setFormData({ 
                    ...formData, 
                    brochure_url: url,
                    brochure_name: filename || formData.brochure_name
                  })}
                  accept=".pdf,.doc,.docx"
                  label="Product Brochure"
                  maxSize={10}
                />
                <div className="mt-4 space-y-2">
                  <Label>Brochure Display Name</Label>
                  <Input 
                    placeholder="Product Brochure" 
                    value={formData.brochure_name || ''}
                    onChange={(e) => setFormData({ ...formData, brochure_name: e.target.value })}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Features Tab */}
          <TabsContent value="features" className="space-y-4 mt-4">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Feature (Indonesian)</Label>
                  <Input 
                    placeholder="Fitur dalam bahasa Indonesia" 
                    value={newFeature.text_id}
                    onChange={(e) => setNewFeature({ ...newFeature, text_id: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Feature (English)</Label>
                  <Input 
                    placeholder="Feature in English" 
                    value={newFeature.text_en}
                    onChange={(e) => setNewFeature({ ...newFeature, text_en: e.target.value })}
                  />
                </div>
              </div>
              <Button 
                type="button" 
                onClick={handleAddFeature}
                className="w-full"
                variant="outline"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Feature
              </Button>
            </div>

            <div className="space-y-2">
              <Label>Current Features ({formData.features.length})</Label>
              {formData.features.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">No features added yet</p>
              ) : (
                formData.features.map((feature) => (
                  <Card key={feature.id}>
                    <CardContent className="p-3 flex justify-between items-start">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{feature.text_en}</p>
                        <p className="text-xs text-gray-600">{feature.text_id}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveFeature(feature.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Benefits Tab */}
          <TabsContent value="benefits" className="space-y-4 mt-4">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Benefit (Indonesian)</Label>
                  <Input 
                    placeholder="Manfaat dalam bahasa Indonesia" 
                    value={newBenefit.text_id}
                    onChange={(e) => setNewBenefit({ ...newBenefit, text_id: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Benefit (English)</Label>
                  <Input 
                    placeholder="Benefit in English" 
                    value={newBenefit.text_en}
                    onChange={(e) => setNewBenefit({ ...newBenefit, text_en: e.target.value })}
                  />
                </div>
              </div>
              <Button 
                type="button" 
                onClick={handleAddBenefit}
                className="w-full"
                variant="outline"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Benefit
              </Button>
            </div>

            <div className="space-y-2">
              <Label>Current Benefits ({formData.benefits.length})</Label>
              {formData.benefits.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">No benefits added yet</p>
              ) : (
                formData.benefits.map((benefit) => (
                  <Card key={benefit.id}>
                    <CardContent className="p-3 flex justify-between items-start">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{benefit.text_en}</p>
                        <p className="text-xs text-gray-600">{benefit.text_id}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveBenefit(benefit.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Tech Specs Tab */}
          <TabsContent value="specs" className="space-y-6">
            <div className="space-y-4">
              {formData.technical_specs.map((spec, index) => (
                <Card key={index} className="p-4">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium">Technical Spec #{index + 1}</h4>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newSpecs = formData.technical_specs.filter((_, i) => i !== index);
                          setFormData({ ...formData, technical_specs: newSpecs });
                        }}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div>
                      <Label>Technical Spec (Indonesian)</Label>
                      <Input 
                        placeholder="e.g., Platform Web & Mobile"
                        value={spec.text_id}
                        onChange={(e) => {
                          const newSpecs = [...formData.technical_specs];
                          newSpecs[index].text_id = e.target.value;
                          setFormData({ ...formData, technical_specs: newSpecs });
                        }}
                      />
                    </div>
                    
                    <div>
                      <Label>Technical Spec (English)</Label>
                      <Input 
                        placeholder="e.g., Web & Mobile Platform"
                        value={spec.text_en}
                        onChange={(e) => {
                          const newSpecs = [...formData.technical_specs];
                          newSpecs[index].text_en = e.target.value;
                          setFormData({ ...formData, technical_specs: newSpecs });
                        }}
                      />
                    </div>
                  </div>
                </Card>
              ))}
              
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const newSpecs = [...formData.technical_specs, { 
                    text_id: '', 
                    text_en: '', 
                    display_order: formData.technical_specs.length 
                  }];
                  setFormData({ ...formData, technical_specs: newSpecs });
                }}
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Technical Specification
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            className="bg-gradient-to-r from-cyan-500 to-teal-600"
            onClick={handleSubmit}
          >
            {product ? 'Update Product' : 'Save Product'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProductFormDialog;
